import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { apiPost } from '../lib/api.js';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [humanToken, setHumanToken] = useState('');
  const [redirecting, setRedirecting] = useState(false);
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const appearance = import.meta.env.VITE_TURNSTILE_APPEARANCE || 'interaction-only';
  const turnstileSize = import.meta.env.VITE_TURNSTILE_SIZE || 'normal';
  const widgetIdRef = useRef(null);
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  // If already authenticated, redirect to dashboard
  // Uses useLayoutEffect for synchronous check before paint
  useLayoutEffect(() => {
    try {
      const token = localStorage.getItem('auth:token');
      if (token) {
        setRedirecting(true);
        navigate('/dashboard', { replace: true });
      }
    } catch (e) { void e; }
  }, [navigate]);

  useEffect(() => {
    if (!siteKey) return;
    const onloadName = 'onTurnstileLoad';
    const ensureRender = () => {
      try {
        const container = document.getElementById('turnstile-forgot');
        if (!window.turnstile || !container) return;
        if (widgetIdRef.current) {
          try { window.turnstile.reset(widgetIdRef.current, { theme: isDark ? 'dark' : 'light' }); } catch (e) { void e; }
          return;
        }
        const id = window.turnstile.render(container, {
          sitekey: siteKey,
          theme: isDark ? 'dark' : 'light',
          appearance,
          size: turnstileSize,
          callback: (token) => setHumanToken(token),
          'error-callback': () => setHumanToken(''),
          'timeout-callback': () => setHumanToken(''),
        });
        widgetIdRef.current = id;
      } catch (e) { void e; }
    };
    if (window.turnstile) { ensureRender(); return; }
    window[onloadName] = () => ensureRender();
    const existing = document.querySelector('script[data-turnstile]');
    if (!existing) {
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=' + onloadName;
      s.async = true;
      s.defer = true;
      s.setAttribute('data-turnstile', '1');
      document.head.appendChild(s);
    }
    return () => {
      try { if (window.turnstile && widgetIdRef.current) window.turnstile.remove(widgetIdRef.current); } catch (e) { void e; }
      widgetIdRef.current = null;
    };
  }, [siteKey, isDark, appearance, turnstileSize]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setLoading(true);
    try {
      if (siteKey && !humanToken) {
        throw new Error('Please complete the human verification.');
      }
      // Endpoint to be implemented on API side: request password reset
      await apiPost('/users/password/request', { email, turnstileToken: humanToken });
      setMessage('If this email exists, a reset link has been sent.');
    } catch (err) {
      // Gracefully handle missing endpoint or errors
      const msg = err?.response?.data?.message || err?.message || 'Unable to process request right now.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (redirecting) return null;
  return (
    <div className="min-h-screen w-full grid grid-cols-1 md:grid-cols-10 items-stretch">
      {/* Left hero */}
      <div className="flex items-center justify-center md:col-span-7 w-full h-full px-8 md:px-12 py-8 bg-transparent">
        <div className="w-full flex items-center justify-center">
          <Logo className="h-72 md:h-80 w-auto" alt="Kreatives" variant="footer" />
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center md:justify-end md:col-span-3">
        <div className="w-full bg-transparent px-4 sm:px-6 md:px-8 py-6 md:py-8 max-w-md mx-auto md:mx-0 md:ml-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-primary-text">Forgot your password?</h1>
            <p className="mt-2 text-sm text-secondary-text">
              Enter your email and we’ll send you a reset link.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-primary-text mb-1">Email</label>
              <div className="relative">
                <svg aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary-text" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2 6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2Zm2 0 8 5 8-5H4Zm16 12V8l-8 5-8-5v10h16Z" />
                </svg>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="w-full bg-input-bg border border-card-border rounded-lg pl-10 pr-4 py-3 text-primary-text placeholder-secondary-text focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            {message && (
              <div className="text-sm text-green-600" role="status">{message}</div>
            )}
            {error && (
              <div className="text-sm text-red-500" role="alert">{error}</div>
            )}

            {siteKey && (
              <div className="pt-2">
                <div id="turnstile-forgot" className="inline-block" />
              </div>
            )}

            <button type="submit" className="cta-brand w-full py-3" data-variant="primary" disabled={loading || (!!siteKey && !humanToken)}>
              {loading ? '…' : 'Send Reset Link'}
            </button>

            <p className="mt-2 text-sm text-secondary-text">
              Remembered your password?{' '}
              <Link to="/login" className="text-brand-600 dark:text-brand-400 hover:underline hover:text-brand-700 dark:hover:text-brand-300">Sign in</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
